'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { FOCUS_RING } from '@common/components/interaction';
import SvgWarningFill from '@common/icon/SvgWarningFill';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { CustomLink } from '@/app/_components/CustomLink';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { calculateRequiredDonationAmount } from '@/app/donation/_utils/giftOrder';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { useGiftOption } from '../../../_context/GiftOptionContext';
import { DELIVERY_PERIOD_LABEL } from '../../../constants';
import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { PrefillForm } from '../PrefillForm';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';
interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: GiftProductDetail;
  className?: string;
}
export const HeaderInfoSection = t0 => {
  const $ = _c(89);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    productData,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const {
    selectedOptions
  } = useGiftOption();
  let t1;
  if ($[0] !== selectedOptions) {
    t1 = Array.from(selectedOptions.values()).reduce(_temp, 0);
    $[0] = selectedOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalExtraProductPrice = t1;
  let t2;
  if ($[2] !== productData?.currentPrice || $[3] !== requiredDonationAmount || $[4] !== selectedOptions.size || $[5] !== totalExtraProductPrice) {
    t2 = selectedOptions.size > 0 ? calculateRequiredDonationAmount((productData?.currentPrice ?? 0) + totalExtraProductPrice) : requiredDonationAmount;
    $[2] = productData?.currentPrice;
    $[3] = requiredDonationAmount;
    $[4] = selectedOptions.size;
    $[5] = totalExtraProductPrice;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const selectedDonationAmount = t2;
  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;
  const institutionRouteId = productData?.institutionSlugId ?? productData?.institutionId;
  let t3;
  if ($[7] !== onlineProductYN || $[8] !== productData?.deliveryExclusionRegionList) {
    t3 = onlineProductYN ? "" : (productData?.deliveryExclusionRegionList ?? []).map(_temp2).join(", ");
    $[7] = onlineProductYN;
    $[8] = productData?.deliveryExclusionRegionList;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const deliveryExclusionRegionNames = t3;
  const t4 = reviewRating ?? 0;
  const t5 = isDesktopViewport ? 24 : 20;
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <Rating value={t4} starCount={1} size={t5} />;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4");
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = reviewRating ?? 0;
  let t9;
  if ($[14] !== t8) {
    t9 = t8.toFixed(1);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== reviewCount) {
    t10 = reviewCount.toLocaleString();
    $[16] = reviewCount;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <span className={t7}>{t9}/5({t10})</span>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t6) {
    t12 = <>{t6}{t11}</>;
    $[21] = t11;
    $[22] = t6;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const ratingSummary = t12;
  let t13;
  if ($[24] !== className) {
    t13 = cn("grid grid-cols-2 gap-28", "tablet:grid-cols-1 tablet:gap-0", "mobile:grid-cols-1 mobile:gap-0", className);
    $[24] = className;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== adultYN || $[27] !== heroImageUrlList) {
    t14 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[26] = adultYN;
    $[27] = heroImageUrlList;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== adultYN || $[30] !== heroImageUrlList || $[31] !== title) {
    t15 = <DesktopMainImages title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[29] = adultYN;
    $[30] = heroImageUrlList;
    $[31] = title;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20", "mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:pt-24");
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== institutionName || $[35] !== institutionRouteId) {
    t17 = institutionRouteId ? <CustomLink href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: institutionRouteId
    })} aria-label={`${institutionName} 상세 페이지로 이동`} className={cn("w-fit rounded-xsmall font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4", "hover:underline active:opacity-80", FOCUS_RING)}>{institutionName}</CustomLink> : <p className={cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4")}>{institutionName}</p>;
    $[34] = institutionName;
    $[35] = institutionRouteId;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ShareButton />;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t17) {
    t19 = <div className="mb-8 flex items-center justify-between gap-4">{t17}{t18}</div>;
    $[38] = t17;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("mb-8 flex justify-between gap-16", "mobile-tablet:mb-16");
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = isStoppedSale ? "text-Gray-400" : "text-Gray-900 mobile-tablet:text-Gray-black";
  let t22;
  if ($[41] !== t21) {
    t22 = cn("flex-1 break-all font-700 typo-h4", "tablet:font-600 tablet:typo-body1", "mobile:font-600 mobile:typo-body1", t21);
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== productName || $[44] !== t22) {
    t23 = <div className={t20}><h1 className={t22}>{productName}</h1></div>;
    $[43] = productName;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = cn("mb-28 flex flex-wrap items-center gap-8", "mobile-tablet:mb-16 mobile-tablet:gap-4");
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== selectedDonationAmount) {
    t25 = selectedDonationAmount === 100000 && <span className="rounded-xsmall bg-G-50 px-12 py-4 font-700 text-G-500 typo-caption1">기부하는 10만원, 모두 세액공제!</span>;
    $[47] = selectedDonationAmount;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== ratingSummary || $[50] !== reviewCount || $[51] !== scrollToSectionWithTab) {
    t26 = reviewCount > 0 ? <button type="button" onClick={() => scrollToSectionWithTab("review")} className={cn("ml-auto flex items-center gap-4 rounded-xsmall transition-opacity duration-200", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")}>{ratingSummary}</button> : <div className="ml-auto flex items-center gap-4">{ratingSummary}</div>;
    $[49] = ratingSummary;
    $[50] = reviewCount;
    $[51] = scrollToSectionWithTab;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t25 || $[54] !== t26) {
    t27 = <div className={t24}>{t25}{t26}</div>;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== selectedDonationAmount) {
    t28 = <div className="mb-20"><PriceInfo amount={selectedDonationAmount} /></div>;
    $[56] = selectedDonationAmount;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t19 || $[59] !== t23 || $[60] !== t27 || $[61] !== t28) {
    t29 = <div className="flex flex-col px-24">{t19}{t23}{t27}{t28}</div>;
    $[58] = t19;
    $[59] = t23;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = cn("flex flex-col gap-24 border-t border-Gray-200 p-20", "tablet:gap-16", "mobile:gap-16");
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== onlineProductDesc || $[65] !== onlineProductYN) {
    t31 = onlineProductYN ? <div className={cn("flex flex-col gap-12 typo-body3", "tablet:typo-body4", "mobile:typo-body4")}><div className="flex items-start gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">배송정보</p><p className="font-400 text-Gray-900">온라인 답례품</p></div>{onlineProductDesc ? <div className="flex items-start gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">답례품 설명</p><p className="whitespace-pre-line font-400 text-Gray-900">{onlineProductDesc}</p></div> : null}</div> : <div className={cn("flex flex-col gap-12 typo-body3", "tablet:typo-body4", "mobile:typo-body4")}><div className="flex items-center gap-16"><p className="w-92 font-400 text-Gray-600">배송정보</p><p className="font-400 text-Gray-900">무료배송</p></div><div className="flex gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">배송안내</p><div className="flex flex-col gap-4"><p className="font-400 text-Gray-900">{DELIVERY_PERIOD_LABEL}</p><p className="font-500 text-Gray-900">(식품의 경우 지연 발생 시 1주~2주 소요)</p></div></div></div>;
    $[64] = onlineProductDesc;
    $[65] = onlineProductYN;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== deliveryExclusionRegionNames || $[68] !== exchangeYN) {
    t32 = !exchangeYN || deliveryExclusionRegionNames ? <ul className="flex flex-col gap-8 rounded-xsmall border border-Gray-300 px-20 py-12 font-400 text-Gray-600 typo-caption1">{!exchangeYN ? <li className="flex items-center gap-4"><SvgWarningFill className="size-13 shrink-0 text-B-500" /><p>해당 답례품은 반품/교환이 불가해요.</p></li> : null}{deliveryExclusionRegionNames ? <li className="flex items-start gap-4"><SvgWarningFill className="mt-3 size-13 shrink-0 text-B-500" /><p className="shrink-0">배송불가 지역</p><span aria-hidden={true} className="text-Gray-500 typo-caption2">|</span><p className="text-Gray-900">{deliveryExclusionRegionNames}</p></li> : null}</ul> : null;
    $[67] = deliveryExclusionRegionNames;
    $[68] = exchangeYN;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t31 || $[71] !== t32) {
    t33 = <div className={t30}>{t31}{t32}</div>;
    $[70] = t31;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== productData || $[74] !== productNumber) {
    t34 = <PrefillForm className="px-20" productData={productData} productNumber={productNumber} />;
    $[73] = productData;
    $[74] = productNumber;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Skeleton className="h-56 w-full rounded-medium" />;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== productNumber) {
    t36 = <div className="hidden px-20 pt-24 mobile-tablet:block"><Suspense fallback={t35}><EventRewardCard productNumber={productNumber} defaultExpanded={true} /></Suspense></div>;
    $[77] = productNumber;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t29 || $[80] !== t33 || $[81] !== t34 || $[82] !== t36) {
    t37 = <div className={t16}><section className="flex w-full flex-col">{t29}{t33}{t34}{t36}</section></div>;
    $[79] = t29;
    $[80] = t33;
    $[81] = t34;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t13 || $[85] !== t14 || $[86] !== t15 || $[87] !== t37) {
    t38 = <div className={t13}>{t14}{t15}{t37}</div>;
    $[84] = t13;
    $[85] = t14;
    $[86] = t15;
    $[87] = t37;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  return t38;
};
function _temp(total, option) {
  return total + option.extraProductPrice;
}
function _temp2(region) {
  return region.regionName;
}